// « Nouvel éditeur mobile » — the slim top bar:
//   ← · album title (tap = rename) + save status · Défaire · Refaire · « … »
// Every action is the editor's own handler, passed in; this only draws.
import { useEffect, useRef } from 'react';
import { M2 } from './theme';
import { saveStatusLabel, type M2SaveStatus } from './m2Model';

export function MobileTopBar({
  title,
  titleEditing,
  onTitleEditStart,
  onTitleChange,
  onTitleEditEnd,
  saveStatus,
  everSaved,
  position,
  showEditActions,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onBack,
  onMenu,
  menuOpen,
  compact = false,
}: {
  /** Phone held sideways: a shorter bar (44 px) so the album keeps the room. */
  compact?: boolean;
  title: string;
  titleEditing: boolean;
  onTitleEditStart: () => void;
  onTitleChange: (v: string) => void;
  onTitleEditEnd: () => void;
  saveStatus: M2SaveStatus;
  everSaved: boolean;
  /** « Page 4 / 36 » — where the customer is. */
  position?: string;
  /** false on a locked album: no undo / redo, no rename. */
  showEditActions: boolean;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onBack: () => void;
  onMenu: () => void;
  menuOpen: boolean;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (titleEditing) inputRef.current?.focus();
  }, [titleEditing]);
  const status = saveStatusLabel(saveStatus, everSaved);
  const statusColor = saveStatus === 'error' ? M2.danger : saveStatus === 'saving' ? M2.muted : M2.success;
  const iconBtn = 'w-11 h-11 shrink-0 rounded-full flex items-center justify-center active:scale-95 transition-transform disabled:opacity-35';

  return (
    <header
      data-m2-topbar=""
      className="shrink-0 flex items-center gap-0.5 px-1 z-30"
      style={{ height: compact ? 44 : 56, background: M2.surface, borderBottom: `1px solid ${M2.gold}26`, color: M2.ink }}
    >
      <button type="button" className={iconBtn} onClick={onBack} aria-label="Retour" title="Retour">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>

      <div className="flex-1 min-w-0 flex flex-col justify-center px-1">
        {titleEditing && showEditActions ? (
          <input
            ref={inputRef}
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            onBlur={onTitleEditEnd}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') onTitleEditEnd(); }}
            aria-label="Nom de l'album"
            className="w-full min-h-11 px-2 rounded-lg outline-none font-semibold"
            // ≥ 16 px: iOS does not zoom the page on focus
            style={{ fontSize: 16, color: M2.ink, border: `1px solid ${M2.gold}`, background: M2.panel }}
          />
        ) : (
          <button
            type="button"
            onClick={showEditActions ? onTitleEditStart : undefined}
            disabled={!showEditActions}
            className="min-h-11 w-full text-left flex flex-col justify-center disabled:cursor-default"
            aria-label={showEditActions ? `Renommer l'album « ${title} »` : title}
            title={showEditActions ? 'Renommer' : undefined}
          >
            <span className="block truncate font-semibold leading-tight" style={{ fontSize: 15 }}>{title}</span>
            <span className="block truncate leading-tight mt-0.5" style={{ fontSize: 12 }}>
              {status && <span style={{ color: statusColor }} data-m2-save-status={saveStatus}>{saveStatus === 'saved' || (saveStatus === 'idle' && everSaved) ? '✓ ' : ''}{status}</span>}
              {status && position ? <span style={{ color: M2.muted }}> · </span> : null}
              {position && <span style={{ color: M2.muted }}>{position}</span>}
            </span>
          </button>
        )}
      </div>

      {showEditActions && (
        <>
          <button type="button" className={iconBtn} onClick={onUndo} disabled={!canUndo} aria-label="Défaire" title="Défaire">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M9 14L4 9l5-5" /><path d="M4 9h10a6 6 0 010 12h-3" />
            </svg>
          </button>
          <button type="button" className={iconBtn} onClick={onRedo} disabled={!canRedo} aria-label="Refaire" title="Refaire">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M15 14l5-5-5-5" /><path d="M20 9H10a6 6 0 000 12h3" />
            </svg>
          </button>
        </>
      )}
      <button
        type="button"
        className={iconBtn}
        onClick={onMenu}
        aria-label="Plus d'options"
        aria-expanded={menuOpen}
        title="Plus d'options"
        style={menuOpen ? { background: `${M2.gold}22` } : undefined}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" />
        </svg>
      </button>
    </header>
  );
}
